import {
  StartStreamTranscriptionCommand,
  StartStreamTranscriptionCommandInput,
  TranscribeStreamingClient,
} from "@aws-sdk/client-transcribe-streaming";
import { useLogin } from "ui";
import { create } from "zustand";
import { pEventIterator } from "p-event";
import { useCaptionHandler } from "./caption-handler-store";
import { toast } from "react-toastify";

type AICaptionStore = {
  credentials: {
    AccessKeyId: string;
    SecretAccessKey: string;
    SessionToken: string;
    Expiration: string;
  } | null;
  audioWorkletNode: AudioWorkletNode | null;
  transcriptionClient: TranscribeStreamingClient | null;
  active: boolean;
  soundActive: boolean;
  audioDataIterator: AsyncIterableIterator<
    MessageEvent<MessageDataType>
  > | null;
  audiosource: string;
  sampleRate: number;
  languages: Language[];
  error: string;
  getCredentials: () => Promise<void>;
  startStreaming: () => Promise<void>;
  stopStreaming: () => void;
  startAudioCapture: () => Promise<void>;
  stopAudioCapture: () => void;
};

type RecordingProperties = {
  numberOfChannels: number;
  sampleRate: number;
  maxFrameCount: number;
};

type MessageDataType = {
  message: string;
  buffer: Array<Float32Array>;
  recordingLength: number;
};

export const Languages = [
  { id: "sv-SE", name: "Swedish" },
  { id: "en-AU", name: "English (Australian)" },
  { id: "en-GB", name: "English (British)" },
  { id: "en-US", name: "English (American)" },
  { id: "de-DE", name: "German" },
  { id: "es-US", name: "Spanish (Latin America)" },
  { id: "fr-CA", name: "Canadian French" },
  { id: "fr-FR", name: "French" },
  { id: "hi-IN", name: "Hindi" },
  { id: "it-IT", name: "Italian" },
  { id: "ja-JP", name: "Japanese" },
  { id: "ko-KR", name: "Korean" },
  { id: "pt-BR", name: "Brazilian Portuguese" },
  { id: "th-TH", name: "Thai" },
  { id: "zh-CN", name: "Chinese (Mandarin)" },
] as const;

type LanguageCode = (typeof Languages)[number]["id"];
export type Language = { id: LanguageCode; name: string };

function pcmEncode(input: Float32Array) {
  const buffer = new ArrayBuffer(input.length * 2);
  const view = new DataView(buffer);
  for (let i = 0; i < input.length; i++) {
    const s = Math.max(-1, Math.min(1, input[i]));
    view.setInt16(i * 2, s < 0 ? s * 0x8000 : s * 0x7fff, true);
  }
  return buffer;
}

export const useAICaptionStore = create<AICaptionStore>((set, get) => ({
  credentials: null,
  audioWorkletNode: null,
  transcriptionClient: null,
  active: false,
  soundActive: false,
  audioDataIterator: null,
  audiosource: "ScreenCapture",
  sampleRate: 48000,
  languages: [Languages[0]],
  error: "",
  async getCredentials() {
    const token = useLogin.getState().token;
    const response = await fetch("/captiontoken", {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (response.ok) {
      const data = await response.json();
      set({ credentials: data, error: "" });
    } else {
      set({ error: "Failed to get credentials" });
    }
  },
  async startAudioCapture() {
    const audioContext = new window.AudioContext();
    let stream: MediaStream;

    const { audiosource, credentials, sampleRate, languages } = get();

    if (audiosource === "ScreenCapture") {
      stream = await window.navigator.mediaDevices.getDisplayMedia({
        video: true,
        audio: true,
      });
    } else {
      stream = await window.navigator.mediaDevices.getUserMedia({
        video: false,
        audio: true,
      });
    }

    const source1 = audioContext.createMediaStreamSource(stream);

    const recordingprops: RecordingProperties = {
      numberOfChannels: 1,
      sampleRate: audioContext.sampleRate,
      maxFrameCount: (audioContext.sampleRate * 1) / 10,
    };

    try {
      await audioContext.audioWorklet.addModule("./recording-processor.js");
    } catch (error) {
      console.log(`Add module error ${error}`);
    }
    const audioWorkletNode = new AudioWorkletNode(
      audioContext,
      "recording-processor",
      {
        processorOptions: recordingprops,
      }
    );

    const destination = audioContext.createMediaStreamDestination();

    audioWorkletNode.port.postMessage({
      message: "UPDATE_RECORDING_STATE",
      setRecording: true,
    });

    source1.connect(audioWorkletNode).connect(destination);
    audioWorkletNode.port.onmessageerror = (error) => {
      console.log(`Error receving message from worklet ${error}`);
    };

    set({
      audioWorkletNode,
      soundActive: true,
    });
  },
  stopAudioCapture() {
    const { audioWorkletNode, transcriptionClient, audioDataIterator } = get();
    if (audioWorkletNode) {
      audioWorkletNode.port.postMessage({
        message: "UPDATE_RECORDING_STATE",
        setRecording: false,
      });
      audioWorkletNode.port.close();
      audioWorkletNode.disconnect();
    } else {
      console.log("no media recorder available to stop");
    }
    if (transcriptionClient) {
      toast.info("AI-Captioning stopped.");
      transcriptionClient.destroy();
    }
    if (audioDataIterator) {
      audioDataIterator.return?.();
    }
    set({
      audioWorkletNode: null,
      transcriptionClient: null,
      soundActive: false,
      active: false,
      audioDataIterator: null,
    });
  },
  async startStreaming() {
    const {
      audioWorkletNode,
      credentials,
      sampleRate,
      languages,
      audioDataIterator: oldAudioDataIterator,
      active,
    } = get();
    oldAudioDataIterator?.return?.();
    if (!audioWorkletNode) {
      toast.error(
        "No audio worklet node. Have you shared your mic/screen sound?"
      );
      return;
    }
    if (active) {
      toast.error("AI-Captioning is already active.");
      return;
    }

    const transcriptionClient = new TranscribeStreamingClient({
      region: "eu-central-1",
      credentials: {
        accessKeyId: credentials!.AccessKeyId,
        secretAccessKey: credentials!.SecretAccessKey,
        sessionToken: credentials!.SessionToken,
        expiration: new Date(credentials!.Expiration),
      },
    });

    const audioDataIterator = pEventIterator<
      "message",
      MessageEvent<MessageDataType>
    >(audioWorkletNode.port, "message");

    const getAudioStream = async function* () {
      for await (const chunk of audioDataIterator) {
        if (chunk.data.message === "SHARE_RECORDING_BUFFER") {
          const abuffer = pcmEncode(chunk.data.buffer[0]);
          const audiodata = new Uint8Array(abuffer);
          yield {
            AudioEvent: {
              AudioChunk: audiodata,
            },
          };
        }
      }
    };

    const startStreamParams: StartStreamTranscriptionCommandInput = {
      LanguageCode: languages[0]?.id || "sv-SE",
      MediaEncoding: "pcm",
      MediaSampleRateHertz: sampleRate,
      AudioStream: getAudioStream(),
      LanguageOptions: languages.map((lang) => lang?.id).join(","),
      PreferredLanguage: languages[0]?.id,
      VocabularyName: "Svenska-test",
      ShowSpeakerLabel: true,
    };

    if (languages.length > 1) {
      delete startStreamParams.LanguageCode;
      delete startStreamParams.VocabularyName;
      startStreamParams.VocabularyNames = "Svenska-test";
      startStreamParams.IdentifyLanguage = true;
    } else {
      delete startStreamParams.LanguageOptions;
      delete startStreamParams.PreferredLanguage;
    }

    if (!languages.find((lang) => lang.id === "sv-SE")) {
      delete startStreamParams.VocabularyName;
    }

    const command = new StartStreamTranscriptionCommand(startStreamParams);

    const data = await transcriptionClient.send(command);
    toast.info("AI-Captioning started.");
    console.log("Transcribe sesssion established ", data.SessionId);

    set({
      transcriptionClient,
      active: true,
      audioDataIterator,
    });

    const handleTranscriptionEvent =
      useCaptionHandler.getState().handleTranscriptionEvent;

    if (data.TranscriptResultStream) {
      for await (const event of data.TranscriptResultStream) {
        handleTranscriptionEvent(event);
      }
    }
  },
  stopStreaming() {
    const { transcriptionClient, audioDataIterator } = get();
    if (transcriptionClient) {
      toast.info("AI-Captioning stopped.");
      transcriptionClient.destroy();
    }
    if (audioDataIterator) {
      audioDataIterator.return?.();
    }
    set({
      transcriptionClient: null,
      active: false,
      audioDataIterator: null,
    });
  },
}));
